import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Upload, CheckCircle2, Loader2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { api, formatApiErrorDetail } from "../lib/api";
import { trackLeadConversion } from "../lib/gtag";
import { HEADER_IMG } from "../data/site";

const inputCls =
  "w-full bg-card border border-border px-4 py-3 font-body text-foreground placeholder:text-muted-foreground rounded-none focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-shadow";
const labelCls = "block font-mono uppercase text-[11px] tracking-[0.15em] text-muted-foreground mb-2";

function FileField({ name, label, testid, accept }) {
  const [fileName, setFileName] = useState("");
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <label className="flex items-center gap-3 border border-dashed border-border px-4 py-4 cursor-pointer hover:border-brand-orange transition-colors">
        <Upload size={18} className="text-brand-orange" />
        <span className="text-muted-foreground text-sm truncate">{fileName || `Choose a file (${accept})`}</span>
        <input data-testid={testid} type="file" name={name} accept={accept} className="hidden"
          onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} />
      </label>
    </div>
  );
}

export default function SupplierRegistration() {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData(e.target);
      await api.post("/supplier-registration", fd, { headers: { "Content-Type": "multipart/form-data" } });
      trackLeadConversion("supplier-registration");
      setDone(true);
      toast.success("Thank you! Your request has been submitted successfully. Our team will review it and get back to you shortly.");
      e.target.reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="supplier-page">
      <PageHeader
        overline="Supplier Registration"
        title="Become a supplier."
        subtitle="Are you an Indian manufacturer? Register your capabilities and join our audited supplier network."
        bgImage={HEADER_IMG.supplier}
      />
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        {done ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            data-testid="supplier-success" className="border border-border bg-secondary p-12 text-center">
            <CheckCircle2 size={48} className="text-brand-orange mx-auto mb-6" />
            <h2 className="font-display font-black text-3xl mb-3">Thank you!</h2>
            <p className="text-muted-foreground mb-8">Your request has been submitted successfully. Our team will review it and get back to you shortly.</p>
            <button onClick={() => setDone(false)} className="border border-foreground px-6 py-3 font-mono uppercase text-[11px] tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors">Submit Another</button>
          </motion.div>
        ) : (
          <form onSubmit={onSubmit} data-testid="supplier-form" className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div><label className={labelCls}>Company Name *</label><input data-testid="supplier-company" name="company_name" required className={inputCls} placeholder="Acme Industries Pvt Ltd" /></div>
              <div><label className={labelCls}>Contact Person *</label><input data-testid="supplier-contact" name="contact_person" required className={inputCls} placeholder="Full name" /></div>
              <div><label className={labelCls}>Email *</label><input data-testid="supplier-email" type="email" name="email" required className={inputCls} placeholder="you@company.com" /></div>
              <div><label className={labelCls}>Mobile Number *</label><input data-testid="supplier-mobile" name="mobile" required className={inputCls} placeholder="+91 ..." /></div>
              <div><label className={labelCls}>GST Number</label><input data-testid="supplier-gst" name="gst" className={inputCls} placeholder="GSTIN" /></div>
              <div><label className={labelCls}>Manufacturing Location</label><input data-testid="supplier-location" name="manufacturing_location" className={inputCls} placeholder="City, State" /></div>
            </div>
            <div><label className={labelCls}>Product Categories *</label><input data-testid="supplier-category" name="product_category" required className={inputCls} placeholder="e.g. CNC machined parts, castings" /></div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div><label className={labelCls}>Certifications (ISO / IATF)</label><input data-testid="supplier-certifications" name="certifications" className={inputCls} placeholder="e.g. ISO 9001, IATF 16949" /></div>
              <div><label className={labelCls}>Export Experience</label><input data-testid="supplier-export" name="export_experience" className={inputCls} placeholder="e.g. 5 years, USA & EU" /></div>
            </div>
            <div><label className={labelCls}>Production Capacity</label><input data-testid="supplier-capacity" name="production_capacity" className={inputCls} placeholder="e.g. 50,000 units / month" /></div>
            <div className="grid sm:grid-cols-2 gap-6">
              <FileField name="company_profile" label="Upload Company Profile" testid="supplier-file" accept=".pdf,.doc,.docx,.ppt,.pptx" />
              <FileField name="product_catalogue" label="Upload Product Catalogue" testid="supplier-catalogue" accept=".pdf,.doc,.docx,.ppt,.pptx" />
            </div>
            <button data-testid="supplier-submit" type="submit" disabled={loading}
              className="inline-flex items-center gap-2 bg-brand-orange text-white px-8 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-brand-orangeHover transition-colors disabled:opacity-60">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Submitting</> : "Register Now"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
